/**
 * Fetcher & mutator pure functions untuk transaction domain.
 *
 * International & Domestic order — list, detail, advance-filter, mutation
 * (create/update/delete/update-position/reset-manifest/scan).
 */
import http from "@/config/http";

/* ============================================================
 * International Orders
 * ============================================================ */

export async function fetchInternationalOrders(params = {}) {
  const { data } = await http.get("/api/v1/transaction/international-order", {
    params,
  });
  return data;
}

export async function fetchInternationalOrdersWithFilter(params = {}) {
  /* Jika params.position_order_type diisi (e.g. "Agen"/"Kurir"/"Cabang"),
   * fetcher menambahkan path suffix `/get-filter/<type>` agar BE memfilter
   * by posisi order. Field ini di-strip dari query string. */
  const { position_order_type, ...rest } = params || {};
  const url = position_order_type
    ? `/api/v1/transaction/international-order/get-filter/${position_order_type}`
    : "/api/v1/transaction/international-order/get-filter";
  const { data } = await http.get(url, { params: rest });
  return data;
}

export async function fetchInternationalOrdersFilterAllAgen(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-filter-all-agen",
    { params }
  );
  return data;
}

export async function fetchInternationalOrdersFilterSuratJalan(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-filter-surat-jalan",
    { params }
  );
  return data;
}

export async function fetchInternationalOrderById(id) {
  const { data } = await http.get(
    `/api/v1/transaction/international-order/admin/get/${id}`
  );
  return data;
}

export async function fetchInternationalAgenGeneral(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/agen_general",
    { params }
  );
  return data;
}

export async function fetchTotalOrderInternational(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-total-order",
    { params }
  );
  return data;
}

export async function fetchTotalShipmentFee(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-total-shipment-fee",
    { params }
  );
  return data;
}

export async function fetchPelanggan(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-pelanggan",
    { params }
  );
  return data;
}

export async function fetchInternationalOrderLast() {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/last"
  );
  return data;
}

export async function fetchInternationalOrderByField(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-order-by-field",
    { params }
  );
  return data;
}

export async function fetchInternationalOrderSearchByField(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/get-order-search-by-field",
    { params }
  );
  return data;
}

/* Advance search — dipakai layar scan barcode untuk multi-field filter. */
export async function fetchInternationalAdvanceSearch(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/international-order/advance-search",
    { params }
  );
  return data;
}

/* Mutations */

export async function createInternationalOrder(payload) {
  const { data } = await http.post(
    "/api/v1/transaction/international-order/create",
    payload
  );
  return data;
}

export async function updateInternationalOrder({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/transaction/international-order/update/${id}`,
    payload
  );
  return data;
}

export async function deleteInternationalOrder(id) {
  const { data } = await http.delete(
    `/api/v1/transaction/international-order/delete/${id}`
  );
  return data;
}

export async function updatePositionOrderInternational({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/transaction/international-order/update-position-order/${id}`,
    payload
  );
  return data;
}

export async function resetManifestInternational(id) {
  const { data } = await http.put(
    `/api/v1/transaction/international-order/reset-manifest/${id}`
  );
  return data;
}

export async function scanBarcodeInternational({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/transaction/international-order/update-by-scan-barcode/${id}`,
    payload
  );
  return data;
}

export async function importInternationalOrder(payload, config = {}) {
  const { data } = await http.post(
    "/api/v1/transaction/international-order/import",
    payload,
    config
  );
  return data;
}

/* ============================================================
 * Domestic Orders
 * ============================================================ */

export async function fetchDomesticOrders(params = {}) {
  const { data } = await http.get("/api/v1/transaction/domestic-order", {
    params,
  });
  return data;
}

export async function fetchDomesticOrderById(id) {
  const { data } = await http.get(
    `/api/v1/transaction/domestic-order/admin/get/${id}`
  );
  return data;
}

export async function fetchDomesticOrderLast() {
  const { data } = await http.get("/api/v1/transaction/domestic-order/last");
  return data;
}

export async function fetchDomesticOrderByField(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/domestic-order/get-order-by-field",
    { params }
  );
  return data;
}

export async function fetchDomesticOrderSearchByField(params = {}) {
  const { data } = await http.get(
    "/api/v1/transaction/domestic-order/get-order-search-by-field",
    { params }
  );
  return data;
}

/* Mutations */

export async function createDomesticOrder(payload) {
  const { data } = await http.post(
    "/api/v1/transaction/domestic-order/create",
    payload
  );
  return data;
}

export async function updateDomesticOrder({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/transaction/domestic-order/update/${id}`,
    payload
  );
  return data;
}

export async function deleteDomesticOrder(id) {
  const { data } = await http.delete(
    `/api/v1/transaction/domestic-order/delete/${id}`
  );
  return data;
}

export async function updatePositionOrderDomestic({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/transaction/domestic-order/update-position-order/${id}`,
    payload
  );
  return data;
}
